import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

/**
 * Hook for fetching and managing facilities data
 * @param {Object} options - Configuration options
 * @param {number} [options.cityId] - City ID to filter facilities by
 * @param {boolean} [options.autoFetch=true] - Whether to fetch data automatically when cityId changes
 * @returns {Object} Facilities data and functions
 */
const useFacilities = ({ cityId = null, autoFetch = true } = {}) => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch facilities data
  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (cityId) {
        params.city_id = cityId;
      }
      const response = await api.get('/facilities/', { params });
      setFacilities(response.data);
    } catch (err) {
      console.error('Error fetching facilities:', err);
      setError(err.message || 'Failed to fetch facilities');
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  // Get a single facility by ID
  const getFacility = useCallback(async (facilityId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/facilities/${facilityId}`);
      return response.data;
    } catch (err) {
      console.error(`Error fetching facility ${facilityId}:`, err);
      setError(err.message || 'Failed to fetch facility');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new facility
  const createFacility = useCallback(async (facilityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/facilities/', facilityData);
      setFacilities((prevFacilities) => [...prevFacilities, response.data]);
      return response.data;
    } catch (err) {
      console.error('Error creating facility:', err);
      setError(err.message || 'Failed to create facility');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing facility
  const updateFacility = useCallback(async (facilityId, facilityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put(`/facilities/${facilityId}`, facilityData);
      setFacilities((prevFacilities) =>
        prevFacilities.map((facility) =>
          facility.id === facilityId ? response.data : facility
        )
      );
      return response.data;
    } catch (err) {
      console.error(`Error updating facility ${facilityId}:`, err);
      setError(err.message || 'Failed to update facility');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a facility
  const deleteFacility = useCallback(async (facilityId) => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/facilities/${facilityId}`);
      setFacilities((prevFacilities) =>
        prevFacilities.filter((facility) => facility.id !== facilityId)
      );
      return true;
    } catch (err) {
      console.error(`Error deleting facility ${facilityId}:`, err);
      setError(err.message || 'Failed to delete facility');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data when cityId changes if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchFacilities();
    }
  }, [autoFetch, fetchFacilities]);

  return {
    facilities,
    loading,
    error,
    fetchFacilities,
    getFacility,
    createFacility,
    updateFacility,
    deleteFacility,
  };
};

export default useFacilities; 